import { Button, Inline, Stack } from "@sembark-travel/ui/base"
import {
	Form,
	TextInputField,
	PhoneInputField,
	validateFormValues,
	SubmissionError,
	PhoneNumberValidator,
	withServerErrors,
	CheckboxInputField,
} from "@sembark-travel/ui/form"
import { useMemo } from "react"
import * as Validator from "yup"
import { Divider, Col, Grid } from "@sembark-travel/ui/base"
import { IContact } from "./store"

interface AddContactSchema {
	name: string
	email?: string
	designation?: string
	phone_numbers?: Array<{ country_code: string; number: string }>
	is_primary?: boolean | number
}

const InitialValues: AddContactSchema = {
	name: "",
	email: "",
	designation: "",
	phone_numbers: [],
	is_primary: 0,
}

interface AddContactFormProps {
	onCreate: (data: AddContactSchema) => Promise<IContact>
	onCancel?: () => void
	initialValues?: AddContactSchema
	phoneNumberRequired?: boolean
	emailRequired?: boolean
	showPrimaryCheckbox?: boolean
}

export function AddContactForm({
	onCreate,
	onCancel,
	phoneNumberRequired,
	emailRequired,
	showPrimaryCheckbox = false,
	initialValues = InitialValues,
}: AddContactFormProps) {
	const addContactValidationSchema = useMemo(() => {
		const emailValidator = Validator.string().email(
			"Email should be a valid email address"
		)
		return validateFormValues(
			Validator.object()
				.shape({
					name: Validator.string().required("Contact name is required"),
					email: emailRequired
						? emailValidator.required("Please provide an email address")
						: emailValidator,
					phone_numbers: phoneNumberRequired
						? Validator.array()
								.of(PhoneNumberValidator())
								.min(1, "Please provide atleast one phone number")
						: Validator.array().of(PhoneNumberValidator()),
					designation: Validator.string()
						.nullable()
						.max(25, "Please use 25 or fewer characters"),
				})
				.required("Contact data is required")
		)
	}, [phoneNumberRequired, emailRequired])

	return (
		<Form<AddContactSchema>
			initialValues={initialValues}
			validate={addContactValidationSchema}
			onSubmit={withServerErrors(async (values) => {
				const submitValues = {
					...values,
					is_primary: showPrimaryCheckbox ? (values.is_primary ? 1 : 0) : 1,
				}
				await onCreate(submitValues)
				onCancel && onCancel()
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<Grid gap="4">
							<Col>
								<TextInputField
									name="name"
									label="Name"
									type="text"
									required
									placeholder="Johhny Dep"
								/>
							</Col>
							<Col>
								<TextInputField
									name="email"
									label="Email"
									type="email"
									placeholder="user@domain.com"
									secondaryLabel={emailRequired ? undefined : "optional"}
								/>
							</Col>
						</Grid>
						<Inline gap="4" collapseBelow="sm">
							<PhoneInputField
								name="phone_numbers"
								label={`Phone Number(s) ${
									!phoneNumberRequired ? ` (optional)` : ""
								}`}
								multi
								required={phoneNumberRequired}
							/>
							<TextInputField
								name="designation"
								label="Designation"
								type="text"
								placeholder="e.g. Manager"
								secondaryLabel="optional"
							/>
						</Inline>
						{showPrimaryCheckbox ? (
							<Inline
								gap="2"
								padding="3"
								borderWidth="1"
								borderColor="muted"
								rounded="md"
							>
								<CheckboxInputField
									name="is_primary"
									label="Set as Primary Contact"
									help="Primary contact will be used as the default contact for the business."
								/>
							</Inline>
						) : null}
					</Stack>
					<Divider />
					<Stack gap="4">
						<SubmissionError />
						<Inline gap="4">
							<Button disabled={submitting} type="submit">
								{submitting ? "Please wait..." : "Save"}
							</Button>
							{onCancel ? (
								<Button
									onClick={onCancel}
									level="tertiary"
									disabled={submitting}
								>
									Cancel
								</Button>
							) : null}
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

export default AddContactForm
